import { Component, type ErrorInfo, type ReactNode } from "react";
import i18n from "../i18n";

interface Props {
  children: ReactNode;
  /** When this changes (for example the route), a shown error is cleared so the next page gets a fresh start */
  resetKey?: string;
}

/**
 * Keeps a page that fails to load or render from leaving a blank screen with a spinner:
 * the header stays and the person gets a message and a way to reload (for example after an update
 * made the old page files unavailable).
 */
export default class ErrorBoundary extends Component<Props, { error?: Error }> {
  state: { error?: Error } = {};

  static getDerivedStateFromError(error: Error) {
    return { error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error("Page error:", error, info.componentStack);
  }

  componentDidUpdate(prev: Props) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: undefined });
  }

  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div role="alert" className="mx-auto max-w-lg rounded-lg border border-red-200 bg-red-50 p-6 text-center text-red-900">
        <h2 className="text-lg font-semibold">{i18n.t("common.errorPageTitle")}</h2>
        <p className="mt-2">{i18n.t("common.errorPageText")}</p>
        <button type="button" className="btn btn-primary mt-4" onClick={() => window.location.reload()}>
          {i18n.t("common.reloadPage")}
        </button>
      </div>
    );
  }
}
